import { useState } from 'react'

import { ApolloCache } from '@apollo/client'
import { Button, Stack } from '@mui/material'
import { GridFooterContainer } from '@mui/x-data-grid'
import { toast } from 'react-toastify'

import {
  CreateBidAllocationDtoInput,
  CreateBidAllocationMutation,
  useCreateBidAllocationMutation,
  useGetPagedSuppliersBookingAllocatedDriversQuery,
} from 'generated/graphql'
import { GET_PAGED_SUPPLIERS_BOOKING_ALLOCATED_DRIVERS } from 'graphql/queries'

import { SupplierAllocateDialog } from './SupplierAllocateDialog'

interface ICustomGridFooterProps {
  bookingId: number
  queryVariables?: any
}

export const CustomGridFooter = (props: ICustomGridFooterProps) => {
  const { bookingId, queryVariables } = props
  const [open, setOpen] = useState(false)

  // Queries
  const { data: supplierAllocatedDriversData } = useGetPagedSuppliersBookingAllocatedDriversQuery({
    fetchPolicy: 'cache-only',
    variables: {
      ...queryVariables,
    },
  })

  const isAllocationDisabled =
    (supplierAllocatedDriversData?.pagedSupplierBookingAllocatedDrivers?.totalCount ?? 0) > 0

  // Mutations
  const updateAllocation = (cache: ApolloCache<any>, data?: CreateBidAllocationMutation | null) => {
    if (!data?.createBidAllocation) return

    cache.updateQuery(
      {
        query: GET_PAGED_SUPPLIERS_BOOKING_ALLOCATED_DRIVERS,
        variables: {
          ...queryVariables,
        },
      },
      (queryData) => {
        if (queryData) {
          const edges = [
            ...queryData.pagedSupplierBookingAllocatedDrivers.edges,
            {
              __typename: 'PagedSupplierBookingAllocatedDriversEdge',
              node: data.createBidAllocation,
            },
          ]

          const totalCount = queryData.pagedSupplierBookingAllocatedDrivers.totalCount + 1

          return {
            pagedSupplierBookingAllocatedDrivers: {
              ...queryData.pagedSupplierBookingAllocatedDrivers,
              edges,
              totalCount,
            },
          }
        }
      },
    )
  }

  const [allocateDriver, { loading: allocating }] = useCreateBidAllocationMutation({
    onCompleted: (data) => {
      if (data.createBidAllocation) {
        toast.success(`Driver was allocated with success`)
      } else {
        toast.error('Something went wrong. Please try again later.')
      }
      setOpen(false)
    },
    update(cache, { data }) {
      updateAllocation(cache, data)
    },
  })

  // Handlers
  const handleAddClick = () => {
    setOpen(true)
  }

  const handleOnCloseDialog = () => setOpen(false)

  const handleOnAllocated = async (values: CreateBidAllocationDtoInput) => {
    await allocateDriver({
      variables: {
        input: values,
      },
    })
  }

  return (
    <GridFooterContainer>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        justifyContent='space-between'
        alignItems='center'
        py={1}
        px={2}>
        <Button disabled={isAllocationDisabled} onClick={handleAddClick}>
          Add
        </Button>
      </Stack>

      {/* re-rendering dialog to ensure that we always getting fresh list of data */}
      {open && (
        <SupplierAllocateDialog
          openDialog={open}
          onAllocateDriver={handleOnAllocated}
          onCloseDialog={handleOnCloseDialog}
          bookingId={bookingId}
          loading={allocating}
        />
      )}
    </GridFooterContainer>
  )
}
